import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import upilogo from '../../../../assets/upi-logo.svg';
import { useDispatch, useSelector } from 'react-redux';
import { handleAmount } from '../../../../shared/extra';
import { calculatePayment } from '../customPaymentHook';
import { QrCode } from 'lucide-react';
import { setProductDetails } from '../../../../store/productInfo/action';
import CouponDrawer from './CouponDrawer';
import netbanking from '../../../../assets/netBanking.png';
import debit from '../../../../assets/debitcard.png';

function PayFromWallet({ walletPayment, setWalletPayment }) {
  const [netPay, setNetPay] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const userProfile = useSelector((state) => state.UserProfile.allData?.data);
  const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
  const DENOMINATION_STORE_DATA = useSelector((state) => state.Denominations?.allData);
  const balance = userProfile?.balance || 0;

  const paymentMethods = Array.isArray(PRODUCTDETAILS?.payment_methods)
    ? PRODUCTDETAILS.payment_methods
    : [];

  /* ---------- DERIVED FLAGS ---------- */
  const isWalletSelected = walletPayment?.isWalletPayment || false;
  const isWalletEnough = balance >= netPay && netPay > 0;
  const isShowPaymentOptions = isWalletSelected && isWalletEnough && !PRODUCTDETAILS?.selectedPaymentMethod?.id;

  /* ---------- NET PAY ---------- */
  useEffect(() => {
    if (!DENOMINATION_STORE_DATA?.length || !PRODUCTDETAILS) {
      setNetPay(0);
      return;
    }

    const res = calculatePayment(
      DENOMINATION_STORE_DATA,
      PRODUCTDETAILS?.discount,
      PRODUCTDETAILS?.isCashbackProduct
    );

    setNetPay(res?.netPaymentAmt || 0);
  }, [DENOMINATION_STORE_DATA, PRODUCTDETAILS]);

  /* ---------- AUTO SELECTION LOGIC ---------- */
  useEffect(() => {
    if (!PRODUCTDETAILS || !paymentMethods?.length) return;

    const updated = { ...PRODUCTDETAILS };
    const upiMethod = paymentMethods?.find((m) => m.name?.toLowerCase() === 'upi' && m.isActive);

    // If wallet is selected and enough balance, clear selected payment method
    if (isWalletSelected && isWalletEnough) {
      updated.selectedPaymentMethod = null;
      updated.discount = updated.originalDiscount || 0;
      dispatch(setProductDetails(updated));
      return;
    }

    // Auto select UPI if wallet is not selected or insufficient balance
    if (upiMethod && !updated.selectedPaymentMethod) {
      updated.selectedPaymentMethod = upiMethod;
      const discountValue = upiMethod?.showValue > 0 ? upiMethod.showValue : 0;
      updated.discount = discountValue;
      dispatch(setProductDetails(updated));
    }
  }, [isWalletSelected, isWalletEnough, paymentMethods, PRODUCTDETAILS?.originalDiscount]);

  /* ---------- WALLET TOGGLE ---------- */
  const handleWalletSelection = () => {
    // Don't allow toggling if balance is insufficient
    if (balance <= 0 || netPay <= 0) {
      return;
    }

    // Toggle wallet payment state
    const newWalletState = !walletPayment?.isWalletPayment;

    // Update parent component state
    setWalletPayment({
      isWalletPayment: newWalletState,
    });

    // If wallet is being deselected, clear any wallet-related selections
    if (!newWalletState) {
      const updated = { ...PRODUCTDETAILS };
      // Reset discount to original if wallet payment is disabled
      if (updated.selectedPaymentMethod) {
        const discountValue = updated.selectedPaymentMethod?.showValue > 0
          ? updated.selectedPaymentMethod.showValue
          : 0;
        updated.discount = discountValue;
      } else {
        updated.discount = updated.originalDiscount || 0;
      }
      dispatch(setProductDetails(updated));
    }
  };

  /* ---------- PAYMENT METHOD SELECTION ---------- */
  const paymentMethodSelection = (method) => {
    if (!method || !method.isActive) return;

    // Prevent selecting payment method when wallet is selected and has enough balance
    if (isWalletSelected && isWalletEnough) {
      return;
    }

    const updated = { ...PRODUCTDETAILS };

    if (method.id !== PRODUCTDETAILS?.selectedPaymentMethod?.id) {
      updated.selectedPaymentMethod = method;
      const discountValue = method?.showValue > 0 ? method.showValue : 0;
      updated.discount = discountValue;
    }

    dispatch(setProductDetails(updated));
  };

  /* ---------- DYNAMIC ICONS ---------- */
  const getPaymentIcon = (methodName) => {
    const name = methodName?.toLowerCase() || '';

    if (name.includes('upi')) return <Box component="img" src={upilogo} alt="UPI" sx={{ width: 40, height: 'auto' }} />;
    if (name.includes('credit'))
      return <Box component="img" src={debit} alt="Credit Card" style={{ width: 40, height: 'auto' }} />;
    if (name.includes('debit'))
      return <Box component="img" src={debit} alt="Debit Card" style={{ width: 40, height: 'auto' }} />;
    if (name.includes('net'))
      return <Box component="img" src={netbanking} alt="Net Banking" style={{ width: 40, height: 'auto' }} />;

    return <QrCode size={20} stroke="#000" />;
  };

  /* ---------- MOBILE DROPDOWN ---------- */
  const MobilePaymentDropdown = () => (
    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
      <Select
        value={PRODUCTDETAILS?.selectedPaymentMethod?.id ?? ''}
        displayEmpty
        disabled={isWalletSelected && isWalletEnough}
        sx={{ borderRadius: 2, height: 52 }}
        onChange={(e) => {
          const selected = paymentMethods?.find(
            (m) => String(m.id) === String(e.target.value)
          );
          paymentMethodSelection(selected);
        }}
        renderValue={(selectedId) => {
          const method = paymentMethods?.find(
            (m) => String(m.id) === String(selectedId)
          );

          if (!selectedId || !method || !method.isActive) {
            return (
              <Typography color="text.disabled" fontSize={14}>
                Select Payment Method
              </Typography>
            );
          }

          const showLabel = PRODUCTDETAILS?.isCashbackProduct
            ? "Cashback"
            : "Save";

          const percentage = method?.showValue > 0 ? method.showValue : 0;

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {getPaymentIcon(method.name)}
              <Divider orientation="vertical" sx={{ height: 30 }} />
              <Stack spacing={0.2}>
                <Typography fontSize={13} textTransform="capitalize">
                  {method.label}
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                  {showLabel}&nbsp;({percentage}%)
                </Typography>
              </Stack>
            </Stack>
          );
        }}
      >
        {paymentMethods.reduce((acc, method, idx) => {
          acc.push(
            <MenuItem key={method.id} value={method.id} disabled={!method.isActive}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {getPaymentIcon(method.name)}
                <Stack spacing={0.2}>
                  <Typography fontSize={14} textTransform="capitalize">
                    {method.label}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {PRODUCTDETAILS?.isCashbackProduct
                      ? "Cashback"
                      : "Save"}&nbsp;
                    ({method.showValue}%)
                  </Typography>
                </Stack>
              </Stack>
            </MenuItem>
          );
          if (idx !== paymentMethods.length - 1) {
            acc.push(<Divider key={`divider-${method.id}`} />);
          }
          return acc;
        }, [])}
      </Select>
    </FormControl>
  );

  /* ---------- DESKTOP CARDS ---------- */
  const DesktopPaymentMethods = () => (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          md: 'repeat(2, 1fr)',
          lg: 'repeat(2, 1fr)',
        },
      }}
    >
      {paymentMethods?.map((method) => {
        const isSelected = method.id === PRODUCTDETAILS?.selectedPaymentMethod?.id;
        const disabled = !method.isActive || (isWalletSelected && isWalletEnough);

        return (
          <Stack
            key={method.id}
            direction="row"
            alignItems="center"
            spacing={{ sm: 1, md: 1.2, lg: 1 }}
            onClick={() => !disabled && paymentMethodSelection(method)}
            sx={[
              {
                backgroundColor: '#fff',
                borderRadius: 2,
                cursor: 'pointer',
                opacity: 1,
                pointerEvents: 'auto',
                userSelect: 'none',
                height: { sm: 52, md: 56, lg: 60 },
                px: { sm: 1.2, md: 1.5, lg: 2 },
                py: { sm: 0.8, md: 1, lg: 1.5 },
              },
              {
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              },
              disabled && {
                cursor: 'not-allowed',
                opacity: 0.5,
                pointerEvents: 'none',
              },
            ]}
          >
            <Box
              sx={{
                width: { sm: 14, md: 16, lg: 18 },
                height: { sm: 14, md: 16, lg: 18 },
                borderRadius: '50%',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : '#bdbdbd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    width: { sm: 6, md: 8, lg: 10 },
                    height: { sm: 6, md: 8, lg: 10 },
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                width: { sm: 28, md: 32, lg: 45 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {getPaymentIcon(method.name)}
            </Box>

            <Stack spacing={0.2}>
              <Typography
                fontWeight={500}
                fontSize={{ sm: '0.8rem', md: '0.9rem', lg: '1.2rem' }}
                lineHeight={1.2}
                sx={{
                  color: 'textPrimary.main',
                  textTransform: 'capitalize',
                }}
              >
                {method.label}
              </Typography>

              {method?.isActive && (
                <Typography
                  color="text.secondary"
                  fontSize={{ sm: 10, md: 11, lg: 12 }}
                  lineHeight={1.2}
                >
                  {PRODUCTDETAILS?.isCashbackProduct ? 'Cashback' : 'Save'}&nbsp;
                  ({method?.showValue > 0 ? method?.showValue : 0}%)
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );

  /* ---------- RENDER ---------- */
  return (
    <>
      <CouponDrawer productId={PRODUCTDETAILS?.id} />

      <Box sx={{ mt: 2 }}>
        <Typography color={'primary.main'} fontWeight={500}>
          Pay With Wallet
        </Typography>

        <List>
          <ListItem
            sx={{
              border: '1.5px solid',
              borderColor: 'divider',
              borderRadius: 2,
              py: 0.5,
              height: 44,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={400} sx={{ color: 'textPrimary.main' }}>
                Available Balance:
              </Typography>
              <Typography fontWeight={500} sx={{ color: 'textPrimary.main' }}>
                ₹{balance >= 1 ? handleAmount(balance) : '0.00'}
              </Typography>
            </Box>

{console.log("balance - ", balance, balance?.toFixed(2))}

            <Checkbox
              checked={isWalletSelected}
              onChange={handleWalletSelection}
              disabled={netPay >= 0  && balance?.toFixed(2) > 0 ? false : true}
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main',
                },
              }}
            />
          </ListItem>
        </List>
      </Box>

      {PRODUCTDETAILS?.isNewPaymentMethodsActive && (
        <Box sx={{ mt: 2, display: isMobile && isShowPaymentOptions ? 'none' : 'block' }}>
          <Typography color={'primary.main'} fontWeight={500}>
            Select Payment Methods
          </Typography>

          {PRODUCTDETAILS?.isNewPaymentMethodsActive && (
            isMobile ? <MobilePaymentDropdown /> : <DesktopPaymentMethods />
          )}
        </Box>
      )}
    </>
  );
}

export default PayFromWallet;
